// File: src/components/admin/autopilot/AutopilotDetailView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Autopilot User Detail View component for viewing autopilot configuration and activity

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Chip, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, MenuItem, Select,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Snackbar, Alert, IconButton, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, Grid
} from '@mui/material';
import {
    ArrowBack, Pause, PlayArrow, Refresh, ExpandMore,
    Delete, AutoFixHigh
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const TAB_NAMES = ['overview', 'activity', 'cycles', 'escalations', 'json'];

const getTabIndex = (tabName) => {
    const index = TAB_NAMES.indexOf(tabName);
    return index >= 0 ? index : 0;
};

const AutopilotDetailView = () => {
    const { uid, tab } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(getTabIndex(tab));
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Data states
    const [detail, setDetail] = useState(null);
    const [activity, setActivity] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [escalations, setEscalations] = useState([]);
    const [loadError, setLoadError] = useState(null);

    // Action states
    const [actionLoading, setActionLoading] = useState(false);
    const [triggerCycleDialog, setTriggerCycleDialog] = useState(false);
    const [selectedTask, setSelectedTask] = useState('');
    const [overrideLearningDialog, setOverrideLearningDialog] = useState(false);
    const [overrideDuration, setOverrideDuration] = useState('');
    const [overrideAction, setOverrideAction] = useState('force-complete');

    // Activity filters
    const [activityTypeFilter, setActivityTypeFilter] = useState('');
    const [activityDateFrom, setActivityDateFrom] = useState('');
    const [activityDateTo, setActivityDateTo] = useState('');

    // Sync tab from URL
    useEffect(() => {
        setActiveTab(getTabIndex(tab));
    }, [tab]);

    const handleTabChange = useCallback((event, newValue) => {
        setActiveTab(newValue);
        navigate(`/autopilot/${uid}/${TAB_NAMES[newValue]}`, { replace: true });
    }, [uid, navigate]);

    // Data loading
    const loadDetail = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const data = await adminApi.getAutopilotUserDetail(uid);
            setDetail(data);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setLoadError(errorInfo);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [uid]);

    const loadActivity = useCallback(async () => {
        try {
            const data = await adminApi.getAutopilotUserActivity(uid);
            setActivity(data || []);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        }
    }, [uid]);

    const loadCycles = useCallback(async () => {
        try {
            const data = await adminApi.getAutopilotUserCycles(uid);
            setCycles(data || []);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        }
    }, [uid]);

    const loadEscalations = useCallback(async () => {
        try {
            const data = await adminApi.getAutopilotUserEscalations(uid);
            setEscalations(data || []);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        }
    }, [uid]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    useEffect(() => {
        if (activeTab === 1) loadActivity();
        if (activeTab === 2) loadCycles();
        if (activeTab === 3) loadEscalations();
    }, [activeTab, loadActivity, loadCycles, loadEscalations]);

    // Actions
    const handlePause = useCallback(async () => {
        setActionLoading(true);
        try {
            await adminApi.pauseAutopilotUser(uid);
            setSnackbar({ open: true, message: 'Autopilot paused successfully', severity: 'success' });
            await loadDetail();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Pause failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    }, [uid, loadDetail]);

    const handleResume = useCallback(async () => {
        setActionLoading(true);
        try {
            await adminApi.resumeAutopilotUser(uid);
            setSnackbar({ open: true, message: 'Autopilot resumed successfully', severity: 'success' });
            await loadDetail();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Resume failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    }, [uid, loadDetail]);

    const handleForceRecommend = useCallback(async () => {
        setActionLoading(true);
        try {
            await adminApi.forceRecommendAutopilotUser(uid);
            setSnackbar({ open: true, message: 'Forced to recommend mode', severity: 'success' });
            await loadDetail();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Force recommend failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    }, [uid, loadDetail]);

    const handleReleaseForcedMode = useCallback(async () => {
        setActionLoading(true);
        try {
            await adminApi.releaseForcedModeAutopilotUser(uid);
            setSnackbar({ open: true, message: 'Forced mode released', severity: 'success' });
            await loadDetail();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Release failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    }, [uid, loadDetail]);

    const handleTriggerCycle = useCallback(async () => {
        if (!selectedTask) return;
        setActionLoading(true);
        try {
            await adminApi.triggerAutopilotCycle(uid, selectedTask);
            setSnackbar({ open: true, message: `Cycle triggered for task: ${selectedTask}`, severity: 'success' });
            setTriggerCycleDialog(false);
            setSelectedTask('');
            await loadDetail();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Trigger cycle failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    }, [uid, selectedTask, loadDetail]);

    const handleClearEscalation = useCallback(async (entityId) => {
        setActionLoading(true);
        try {
            await adminApi.clearAutopilotEscalation(uid, entityId);
            setSnackbar({ open: true, message: 'Escalation cleared', severity: 'success' });
            await loadEscalations();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Clear escalation failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    }, [uid, loadEscalations]);

    const handleOverrideLearning = useCallback(async () => {
        setActionLoading(true);
        try {
            await adminApi.overrideAutopilotLearning(uid, {
                action: overrideAction,
                duration: overrideDuration || undefined
            });
            setSnackbar({ open: true, message: 'Learning override applied', severity: 'success' });
            setOverrideLearningDialog(false);
            setOverrideDuration('');
            setOverrideAction('force-complete');
            await loadDetail();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Override failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    }, [uid, overrideAction, overrideDuration, loadDetail]);

    // Filtered activity
    const filteredActivity = activity.filter((item) => {
        if (activityTypeFilter && item.type !== activityTypeFilter) return false;
        if (activityDateFrom && new Date(item.timestamp) < new Date(activityDateFrom)) return false;
        if (activityDateTo && new Date(item.timestamp) > new Date(activityDateTo)) return false;
        return true;
    });

    const activityTypes = [...new Set(activity.map((a) => a.type))];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError && loadError.type === 'NOT_FOUND') {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Autopilot user not found</Alert>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/autopilot')} sx={{ mt: 2 }}>
                    Back to Autopilot
                </Button>
            </Box>
        );
    }

    const isPaused = detail?.status === 'paused';
    const isForced = detail?.adminOverride?.forced === true;

    return (
        <Box sx={{ p: 3 }} data-testid="autopilot-detail-view">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/autopilot')} sx={{ mr: 2 }}>
                    Back
                </Button>
                <Typography variant="h4">
                    {detail?.email || uid}
                </Typography>
                {isPaused && (
                    <Chip label="Paused" color="warning" sx={{ ml: 2 }} />
                )}
                {isForced && (
                    <Chip label="Admin Override" color="error" sx={{ ml: 1 }} />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Reload data">
                    <IconButton onClick={loadDetail} disabled={actionLoading}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Overview" data-testid="autopilot-tab-overview" />
                <Tab label="Activity" data-testid="autopilot-tab-activity" />
                <Tab label="Cycles" data-testid="autopilot-tab-cycles" />
                <Tab label="Escalations" data-testid="autopilot-tab-escalations" />
                <Tab label="JSON" />
            </Tabs>

            {/* TAB 0: OVERVIEW */}
            {activeTab === 0 && (
                <Box>
                    {/* User Info */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>User Info</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="text.secondary">Email</Typography>
                                <Typography variant="body1">{detail?.email || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="text.secondary">Plan</Typography>
                                <Typography variant="body1">{detail?.plan || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="text.secondary">Activation Date</Typography>
                                <Typography variant="body1">
                                    {detail?.activationDate ? new Date(detail.activationDate).toLocaleString() : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Current Config */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Current Configuration</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="text.secondary">Mode</Typography>
                                <Chip
                                    label={detail?.config?.mode || '-'}
                                    color={detail?.config?.mode === 'autopilot' ? 'success' : 'default'}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="text.secondary">Budget Config</Typography>
                                <Typography variant="body1">
                                    {detail?.config?.budgetConfig
                                        ? `${detail.config.budgetConfig.type}: ${detail.config.budgetConfig.value}`
                                        : '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="text.secondary">Campaigns</Typography>
                                <Typography variant="body1">
                                    {detail?.config?.campaigns?.length ?? 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="text.secondary">Guardrails</Typography>
                                <Typography variant="body1">
                                    {detail?.config?.guardrails
                                        ? Object.keys(detail.config.guardrails).length + ' rules'
                                        : 'None'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Status */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Status</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="text.secondary">Current Status</Typography>
                                <Chip
                                    label={detail?.status || '-'}
                                    color={isPaused ? 'warning' : 'success'}
                                    size="small"
                                />
                            </Grid>
                            {isForced && (
                                <Grid item xs={12} sm={8}>
                                    <Alert severity="info" sx={{ mt: 0 }}>
                                        Admin override is active.
                                        {detail?.adminOverride?.reason && (
                                            <> Reason: {detail.adminOverride.reason}</>
                                        )}
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {/* Action Buttons */}
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Actions</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {isPaused ? (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={actionLoading ? <CircularProgress size={20} /> : <PlayArrow />}
                                    onClick={handleResume}
                                    disabled={actionLoading}
                                    data-testid="autopilot-btn-resume"
                                >
                                    Resume
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={actionLoading ? <CircularProgress size={20} /> : <Pause />}
                                    onClick={handlePause}
                                    disabled={actionLoading}
                                    data-testid="autopilot-btn-pause"
                                >
                                    Pause
                                </Button>
                            )}

                            {isForced ? (
                                <Button
                                    variant="outlined"
                                    onClick={handleReleaseForcedMode}
                                    disabled={actionLoading}
                                >
                                    Release Forced Mode
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<AutoFixHigh />}
                                    onClick={handleForceRecommend}
                                    disabled={actionLoading}
                                    data-testid="autopilot-btn-force-recommend"
                                >
                                    Force Recommend
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={() => setTriggerCycleDialog(true)}
                                disabled={actionLoading}
                                data-testid="autopilot-btn-trigger-cycle"
                            >
                                Trigger Manual Cycle
                            </Button>

                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setOverrideLearningDialog(true)}
                                disabled={actionLoading}
                            >
                                Override Learning
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* TAB 1: ACTIVITY */}
            {activeTab === 1 && (
                <Box>
                    {/* Filters */}
                    <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Select
                            value={activityTypeFilter}
                            onChange={(e) => setActivityTypeFilter(e.target.value)}
                            displayEmpty
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">All Types</MenuItem>
                            {activityTypes.map((type) => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                        <TextField
                            label="From"
                            type="date"
                            size="small"
                            value={activityDateFrom}
                            onChange={(e) => setActivityDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="To"
                            type="date"
                            size="small"
                            value={activityDateTo}
                            onChange={(e) => setActivityDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Paper>

                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Entity</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Reasoning</TableCell>
                                    <TableCell>Confidence</TableCell>
                                    <TableCell>Sync Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredActivity.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography variant="body2" color="text.secondary">No activity found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredActivity.map((item, index) => (
                                        <TableRow key={item.id || index}>
                                            <TableCell>
                                                {item.timestamp ? new Date(item.timestamp).toLocaleString() : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={item.type} size="small" />
                                            </TableCell>
                                            <TableCell>{item.entity || '-'}</TableCell>
                                            <TableCell>{item.action || '-'}</TableCell>
                                            <TableCell>
                                                <Tooltip title={item.reasoning || ''}>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                        {item.reasoning || '-'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{item.confidence ?? '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.syncStatus || '-'}
                                                    size="small"
                                                    color={item.syncStatus === 'synced' ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* TAB 2: CYCLES */}
            {activeTab === 2 && (
                <Box>
                    {cycles.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">No cycles found</Typography>
                        </Paper>
                    ) : (
                        cycles.map((cycle, index) => (
                            <Accordion key={cycle.cycleId || index}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 100 }}>
                                            {cycle.cycleId || '-'}
                                        </Typography>
                                        <Chip label={cycle.task || '-'} size="small" />
                                        <Typography variant="body2" color="text.secondary">
                                            {cycle.timestamp ? new Date(cycle.timestamp).toLocaleString() : '-'}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Typography variant="body2">
                                            Proposed: {cycle.proposed ?? '-'} | Applied: {cycle.applied ?? '-'}
                                        </Typography>
                                        <Chip
                                            label={cycle.status || '-'}
                                            size="small"
                                            color={cycle.status === 'completed' ? 'success' : cycle.status === 'failed' ? 'error' : 'default'}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary">Reasoning</Typography>
                                            <Typography variant="body2">{cycle.reasoning || 'No reasoning provided'}</Typography>
                                        </Grid>
                                        {cycle.revisions && cycle.revisions.length > 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Revisions</Typography>
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Revision</TableCell>
                                                                <TableCell>Change</TableCell>
                                                                <TableCell>Reason</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {cycle.revisions.map((rev, rIndex) => (
                                                                <TableRow key={rIndex}>
                                                                    <TableCell>{rIndex + 1}</TableCell>
                                                                    <TableCell>{rev.change || '-'}</TableCell>
                                                                    <TableCell>{rev.reason || '-'}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Grid>
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        ))
                    )}
                </Box>
            )}

            {/* TAB 3: ESCALATIONS */}
            {activeTab === 3 && (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Entity</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Optimization Type</TableCell>
                                <TableCell>Failures</TableCell>
                                <TableCell>Last Attempt</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {escalations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary">No escalations</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                escalations.map((esc, index) => (
                                    <TableRow key={esc.entityId || index}>
                                        <TableCell>{esc.entityId || '-'}</TableCell>
                                        <TableCell>
                                            <Chip label={esc.type || '-'} size="small" />
                                        </TableCell>
                                        <TableCell>{esc.optimizationType || '-'}</TableCell>
                                        <TableCell>{esc.failures ?? '-'}</TableCell>
                                        <TableCell>
                                            {esc.lastAttempt ? new Date(esc.lastAttempt).toLocaleString() : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={esc.reason || ''}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                    {esc.reason || '-'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Clear Escalation">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleClearEscalation(esc.entityId)}
                                                    disabled={actionLoading}
                                                    data-testid={`autopilot-btn-clear-escalation-${esc.entityId}`}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* TAB 4: JSON */}
            {activeTab === 4 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Raw Data</Typography>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Configuration</Typography>
                        <pre style={{
                            background: '#f5f5f5',
                            padding: '16px',
                            borderRadius: '4px',
                            overflow: 'auto',
                            maxHeight: '400px',
                            fontSize: '13px'
                        }}>
                            {JSON.stringify(detail?.config || {}, null, 2)}
                        </pre>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Cycle Data</Typography>
                        <pre style={{
                            background: '#f5f5f5',
                            padding: '16px',
                            borderRadius: '4px',
                            overflow: 'auto',
                            maxHeight: '400px',
                            fontSize: '13px'
                        }}>
                            {JSON.stringify(cycles, null, 2)}
                        </pre>
                    </Box>
                </Paper>
            )}

            {/* Trigger Cycle Dialog */}
            <Dialog
                open={triggerCycleDialog}
                onClose={() => setTriggerCycleDialog(false)}
            >
                <DialogTitle>Trigger Manual Cycle</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Select a task to trigger a manual optimization cycle for this user.
                    </Typography>
                    <Select
                        value={selectedTask}
                        onChange={(e) => setSelectedTask(e.target.value)}
                        displayEmpty
                        fullWidth
                        size="small"
                    >
                        <MenuItem value="" disabled>Select a task</MenuItem>
                        {(detail?.config?.campaigns || []).map((campaign) => (
                            <MenuItem key={campaign.id || campaign} value={campaign.id || campaign}>
                                {campaign.name || campaign.id || campaign}
                            </MenuItem>
                        ))}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTriggerCycleDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleTriggerCycle}
                        variant="contained"
                        disabled={!selectedTask || actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={16} /> : null}
                    >
                        Trigger
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Override Learning Dialog */}
            <Dialog
                open={overrideLearningDialog}
                onClose={() => setOverrideLearningDialog(false)}
            >
                <DialogTitle>Override Learning</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Override the learning phase for this autopilot user.
                    </Typography>
                    <Select
                        value={overrideAction}
                        onChange={(e) => setOverrideAction(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="force-complete">Force Complete</MenuItem>
                        <MenuItem value="reset">Reset</MenuItem>
                    </Select>
                    {overrideAction === 'force-complete' && (
                        <TextField
                            label="Duration (e.g. 7d, 24h)"
                            value={overrideDuration}
                            onChange={(e) => setOverrideDuration(e.target.value)}
                            fullWidth
                            size="small"
                            helperText="Optional: specify a custom duration"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverrideLearningDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleOverrideLearning}
                        variant="contained"
                        disabled={actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={16} /> : null}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AutopilotDetailView;
